// ---------------------------------------------------------------------------
// PC 専用の機能（2026-10-14 射場さんの決定、#185、docs/74 §1・docs/44「PC 専用の機能」）
//   スマホ（スマホ版、または幅 767px 以下）では、ここに載せた画面への入口をメニューから**消す**（灰色で出さない）。
//   射場さんが使いながら範囲を広げる＝**ここに1行足すだけ**で、設定のメニュー・アカウントのメニュー・左のメニュー（☰）・
//   画面の中の入口（{vendorQuote}の登録など）から消える（画面は道 to で引く＝apps/web の lib/pcOnly.ts の usePcOnly）。
//   URL を直接開いたとき（メールのリンク・ブックマーク）は、画面の中身を描かず止めの画面「パソコンから操作してください」を出す
//   （2026-10-15 射場さんの決定、#186、docs/75・docs/44 §11。ルートの共通の守り＝apps/web の app.tsx の PcOnlyGuard）。
//
//   スマホに残す（ここに載せない）：ユーザー追加・案内メールからの利用開始・会社の切替・個人設定・文字サイズ（#287 で「招待」の言葉をやめた）
// ---------------------------------------------------------------------------

export type PcOnlyFeature = {
  key: string;
  /** 画面に出す名前（docs と、スマホのメニューの下の1行の元） */
  label: string;
  /**
   * 入口の道。頭から一致（/settings/paper は /settings/paper/quote も）・":名前" は1段の何でも・"?キー" はその問い合わせのキーがあるとき。
   * まだ画面が無い機能は空（作ったら足す）
   */
  paths: readonly string[];
};

export const PC_ONLY_FEATURES: readonly PcOnlyFeature[] = [
  { key: "addCompany", label: "会社の追加（新しい会社の作成）", paths: ["/choose?new"] },
  { key: "plan", label: "料金プラン", paths: ["/account/plan"] },
  // お支払い方法・お支払い履歴は PAY.JP をつなぐ2回目（#69）で作る。作ったら道を足す
  { key: "payment", label: "お支払い方法・お支払い履歴", paths: [] },
  { key: "close", label: "退会", paths: ["/settings/close"] },
  { key: "paper", label: "帳票デザイン", paths: ["/settings/paper"] },
  // データの取込（#268 1.5c-1、docs/68 §1）：取込の手順は PC だけ。履歴と取消（設定 → データの取込・取込の画面の完了）はスマホでも
  { key: "dataImport", label: "データ取込", paths: ["/imports/new"] },
  { key: "vendorQuoteImport", label: "{vendorQuote}の取込", paths: ["/vendor-quotes/new", "/groups/:id/vendor-quotes/new"] },
  // #223（docs/89 §15 の14）：単体送付状（帳票の紙の画面の送付状の枠も PC だけ＝画面が幅で出し分ける）
  { key: "coverLetter", label: "送付状の作成", paths: ["/cover-letters/new"] },
  // 見積依頼の送付の画面（2026-10-21 射場さんの決め＝docs/89 ★16）：送付状の枠と紙を並べて番号を採る。見積依頼書はスマホでは閲覧のみ（docs/88 §14 ヲ）
  { key: "rfqSend", label: "見積依頼の送付", paths: ["/rfqs/:id/send"] },
  // メール送付（#225、docs/91 §3.9）：帳票の紙の画面の右の枠（送信方法・メール）は PC だけ＝画面が幅で出し分ける（道は持たない）。送付の記録はスマホでも見る
  { key: "mailSend", label: "メールの送信", paths: [] },
  // {order}の作成（#233、docs/93 §4.3）：作成画面はスマホ版と 767px 以下は閲覧のみ（画面が幅で出し分ける）。新しく作るのは PC だけ
  { key: "orderCreate", label: "{order}の作成", paths: ["/orders/new"] },
  // 仕入の登録（#233 3a、docs/93 §15.10）：作成画面はスマホ版と 767px 以下は閲覧のみ（画面が幅で出し分ける）。新しく登録するのは PC だけ
  { key: "purchaseCreate", label: "仕入の登録", paths: ["/purchases/new"] },
  // {paymentNotice}の作成（#233 3b、docs/93 §15.6）：作成画面はスマホ版と 767px 以下は閲覧のみ（画面が幅で出し分ける）。新しく作るのは PC だけ
  { key: "paymentNoticeCreate", label: "{paymentNotice}の作成", paths: ["/payment-notices/new"] },
  // 支払の登録（#233 3c、docs/93 §15.5）：窓（仕入の画面・{vendor}の詳細・ホームの支払予定の行）は PC だけ＝画面が幅で出し分ける（道は持たない）。履歴はスマホでも見る
  { key: "vendorPaymentCreate", label: "支払の登録", paths: [] },
];

/** スマホのメニューの下の1行（会社の切替とアカウントのメニューの一番下） */
export const PC_ONLY_NOTE = "会社の追加・料金プラン・退会はパソコンから操作します。";

function splitPath(to: string): { segs: string[]; keys: Set<string> } {
  const [path = "", query = ""] = to.split("?");
  const segs = path.split("/").filter(Boolean);
  const keys = new Set(query.split("&").filter(Boolean).map((kv) => kv.split("=")[0] ?? ""));
  return { segs, keys };
}

/** 道 to（"/settings/paper/quote"・"/choose?switch&new" など）が、PC 専用の入口のどれかに当たるか */
export function pcOnlyFeatureOf(to: string): PcOnlyFeature | null {
  const t = splitPath(to);
  for (const f of PC_ONLY_FEATURES) {
    for (const p of f.paths) {
      const e = splitPath(p);
      if (e.segs.length > t.segs.length) continue;
      if (!e.segs.every((s, i) => s.startsWith(":") || s === t.segs[i])) continue;
      if (![...e.keys].every((k) => t.keys.has(k))) continue;
      return f;
    }
  }
  return null;
}

export const isPcOnlyPath = (to: string): boolean => pcOnlyFeatureOf(to) !== null;
